import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProBadge } from 'components/icons';
import MediaCard from 'components/MediaCard';
import RecommendationStamp from 'components/RecommendationStamp';
import { pushModal } from 'redux/modules/modal';
import { hasYoutubeMostReplayedAccessSelector } from 'redux/modules/pricing';
import { VideoWizardObjective } from '../types';
import { block } from './utils';

interface ObjectiveCardOptionsProps {
  onSelectObjective: (selectedObjectiveStepType: VideoWizardObjective) => void;
}

const ObjectiveCardOptions: React.FC<ObjectiveCardOptionsProps> = ({
  onSelectObjective,
}) => {
  const hasYoutubeMostReplayedAccess = useSelector(
    hasYoutubeMostReplayedAccessSelector,
  );

  const dispatch = useDispatch();

  const handleYouTubeMostReplayedUpgrade = useCallback((): void => {
    dispatch(
      pushModal({
        name: 'YouTubeMostReplayedUpsell',
      }),
    );
  }, [dispatch]);

  return (
    <div className={block('objective-card-options')}>
      <MediaCard
        className={block('objective-card')}
        onClick={() => onSelectObjective('SingleClip')}
      >
        <MediaCard.MediaCardImage imgSrc="https://static.sparemin.com/static/webapp-assets/video-transcript-wizard/video-podcast-manual-clips-202505231750.png" />

        <MediaCard.MediaCardContent
          title="Get a single video with captions"
          description="Easily add captions in your preferred style to any video podcast. Apply them to the entire episode or select a specific clip."
          buttonContent="Select an episode →"
          className={block('objective-card-content')}
        />
      </MediaCard>

      <MediaCard
        className={block('objective-card')}
        onClick={() => onSelectObjective('MultipleClips')}
      >
        <MediaCard.MediaCardImage imgSrc="https://static.sparemin.com/static/webapp-assets/video-transcript-wizard/video-podcast-suggested-clips-202505231750.png" />

        <MediaCard.MediaCardContent
          title="Get multiple clips with captions"
          description="Select an entire video podcast episode and we’ll turn it into multiple shareable clips with captions in your preferred style."
          buttonContent="Select an episode →"
          className={block('objective-card-content')}
        />
      </MediaCard>

      <MediaCard
        className={block('objective-card')}
        onClick={() =>
          !hasYoutubeMostReplayedAccess
            ? handleYouTubeMostReplayedUpgrade()
            : onSelectObjective('YouTubeMostReplayed')
        }
      >
        <MediaCard.MediaCardImage imgSrc="https://static.sparemin.com/static/webapp-assets/video-transcript-wizard/video-podcast-youtube-most-replayed-clips-202505231750.png" />

        <MediaCard.MediaCardContent
          stamp={
            !hasYoutubeMostReplayedAccess && (
              <RecommendationStamp description="Available on Pro subscriptions" />
            )
          }
          title={
            <span>
              Get clips with captions for your most replayed content
              {!hasYoutubeMostReplayedAccess && (
                <ProBadge className={block('objective-card-pro-badge')} />
              )}
            </span>
          }
          description="Link a Youtube video and automatically get clips that feature your most replayed content."
          buttonContent={
            !hasYoutubeMostReplayedAccess
              ? 'upgrade to unlock →'
              : 'Select an episode →'
          }
          className={block('objective-card-content')}
        />
      </MediaCard>
    </div>
  );
};

export default ObjectiveCardOptions;
